import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { adminApi, type AdminActionRow } from '@/services/adminApi';
import { useAdminCursorList } from '@/hooks/admin/useAdminCursorList';
import { useAdminExportCsv } from '@/hooks/admin/useAdminExportCsv';
import { AdminListHeader, ActiveFiltersBar } from '@/components/admin/AdminListToolbar';
import { AdminListFooter } from '@/components/admin/AdminListFooter';

/**
 * AdminAuditPage — cross-tenant view of every mutation performed via
 * the Backoffice. Reads from `admin_actions/` (the high-signal trail
 * with before/after diff). For the no-op read trail see the per-customer
 * audit panel that surfaces `admin_audit/`.
 */
export default function AdminAuditPage() {
  const [adminUid, setAdminUid] = useState('');
  const [action, setAction] = useState('');
  const [kind, setKind] = useState('all');
  const [targetId, setTargetId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [searchInline, setSearchInline] = useState('');

  const filterParams = useMemo(() => ({
    ...(adminUid ? { adminUid } : {}),
    ...(action ? { action } : {}),
    ...(kind !== 'all' ? { kind } : {}),
    ...(targetId ? { targetId } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  }), [adminUid, action, kind, targetId, from, to]);

  const { exportCsv, isExporting } = useAdminExportCsv('admin-actions', filterParams);

  const fetchPage = useCallback(
    (cursor: string | null) => adminApi.adminActions.list({ ...filterParams, cursor }),
    [filterParams],
  );
  const {
    items, totalCount, loading, isRefreshing, error, refresh,
    hasMore, loadingMore, loadMore,
  } = useAdminCursorList<AdminActionRow>(fetchPage, [filterParams]);
  const filtered = useMemo(() => {
    if (!searchInline) return items;
    const s = searchInline.toLowerCase();
    return items.filter((r) =>
      r.action.toLowerCase().includes(s)
      || (r.adminEmail ?? '').toLowerCase().includes(s)
      || (r.target?.label ?? '').toLowerCase().includes(s)
      || (r.target?.id ?? '').toLowerCase().includes(s)
      || (r.reason ?? '').toLowerCase().includes(s),
    );
  }, [items, searchInline]);

  const activeFilters: Array<{ key: string; label: string; onClear: () => void }> = [];
  if (adminUid) activeFilters.push({ key: 'admin', label: `Admin: ${adminUid.slice(0, 8)}…`, onClear: () => setAdminUid('') });
  if (action) activeFilters.push({ key: 'action', label: `Action: ${action}`, onClear: () => setAction('') });
  if (kind !== 'all') activeFilters.push({ key: 'kind', label: `Kind: ${kind}`, onClear: () => setKind('all') });
  if (targetId) activeFilters.push({ key: 'target', label: `Target: ${targetId.slice(0, 10)}…`, onClear: () => setTargetId('') });
  if (from) activeFilters.push({ key: 'from', label: `From: ${from}`, onClear: () => setFrom('') });
  if (to) activeFilters.push({ key: 'to', label: `To: ${to}`, onClear: () => setTo('') });
  if (searchInline) activeFilters.push({ key: 'q', label: `Search: "${searchInline}"`, onClear: () => setSearchInline('') });

  const clearAll = () => {
    setAdminUid(''); setAction(''); setKind('all'); setTargetId(''); setFrom(''); setTo(''); setSearchInline('');
  };

  return (
    <div className="space-y-4 max-w-6xl">
      <AdminListHeader
        title="Audit log"
        onRefresh={refresh}
        isRefreshing={isRefreshing}
        onExportCsv={exportCsv}
        isExporting={isExporting}
      />

      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search loaded rows (action, admin, label, reason)..."
          value={searchInline}
          onChange={(e) => setSearchInline(e.target.value)}
          className="h-9 w-[300px]"
        />
        <Input
          placeholder="Action (e.g. user.update)"
          value={action}
          onChange={(e) => setAction(e.target.value)}
          className="h-9 w-[200px]"
        />
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="h-9 w-[150px]"><SelectValue placeholder="Kind" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any kind</SelectItem>
            <SelectItem value="user">User</SelectItem>
            <SelectItem value="company">Company</SelectItem>
            <SelectItem value="subscription">Subscription</SelectItem>
            <SelectItem value="posting">Posting</SelectItem>
            <SelectItem value="position">Position</SelectItem>
            <SelectItem value="plan">Plan</SelectItem>
          </SelectContent>
        </Select>
        <Input
          placeholder="Admin uid"
          value={adminUid}
          onChange={(e) => setAdminUid(e.target.value)}
          className="h-9 w-[220px] font-mono text-xs"
        />
        <Input
          placeholder="Target id"
          value={targetId}
          onChange={(e) => setTargetId(e.target.value)}
          className="h-9 w-[220px] font-mono text-xs"
        />
        <div className="flex items-center gap-1">
          <span className="text-xs text-muted-foreground">When</span>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-[150px]" />
          <span className="text-xs text-muted-foreground">→</span>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-[150px]" />
        </div>
      </div>

      <ActiveFiltersBar filters={activeFilters} onClearAll={clearAll} />

      {error && <p className="text-sm text-destructive">{error}</p>}

      <Card className="border-border/50">
        <CardContent className="p-0">
          {loading && <div className="py-12 text-center text-sm text-muted-foreground">Loading...</div>}
          {!loading && filtered.length === 0 && (
            <div className="py-12 text-center text-sm text-muted-foreground">No admin actions match.</div>
          )}
          <ul className="divide-y divide-border/30">
            {filtered.map((row) => (
              <AuditRow key={row.id} row={row} />
            ))}
          </ul>
          <AdminListFooter
            loadedCount={items.length}
            filteredCount={searchInline ? filtered.length : undefined}
            totalCount={totalCount}
            entityLabel="azione"
            entityLabelPlural="azioni"
            hasMore={hasMore}
            loadingMore={loadingMore}
            onLoadMore={loadMore}
          />
        </CardContent>
      </Card>
      {isRefreshing && <p className="text-xs text-muted-foreground text-right">Aggiornamento...</p>}
    </div>
  );
}

function AuditRow({ row }: { row: AdminActionRow }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <li className="px-5 py-3">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center gap-3 text-left"
      >
        <span className="shrink-0 text-muted-foreground">
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </span>
        <Badge variant="outline" className="font-mono text-xs">{row.action}</Badge>
        <span className="text-sm font-medium truncate">
          {row.target.kind === 'company' ? (
            <Link
              to={`/admin/customers/${row.target.id}`}
              onClick={(e) => e.stopPropagation()}
              className="hover:underline"
            >
              {row.target.label ?? row.target.id}
            </Link>
          ) : row.target.kind === 'user' ? (
            <Link
              to={`/admin/users/${row.target.id}`}
              onClick={(e) => e.stopPropagation()}
              className="hover:underline"
            >
              {row.target.label ?? row.target.id}
            </Link>
          ) : (
            <span>{row.target.label ?? row.target.id}</span>
          )}
        </span>
        <span className="text-xs text-muted-foreground ml-auto truncate max-w-[180px]">{row.adminEmail ?? row.adminUid.slice(0, 12)}</span>
        <span className="text-xs text-muted-foreground shrink-0">
          {row.at ? new Date(row.at).toLocaleString('en-GB') : '—'}
        </span>
      </button>
      {expanded && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 pl-6">
          {row.reason && (
            <div className="md:col-span-2 text-xs">
              <span className="font-medium">Reason: </span>
              <span className="text-muted-foreground">{row.reason}</span>
            </div>
          )}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Before</p>
            <pre className="bg-muted/30 rounded p-2 text-xs overflow-x-auto max-h-[240px]">
              {row.before ? JSON.stringify(row.before, null, 2) : '—'}
            </pre>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">After</p>
            <pre className="bg-muted/30 rounded p-2 text-xs overflow-x-auto max-h-[240px]">
              {row.after ? JSON.stringify(row.after, null, 2) : '—'}
            </pre>
          </div>
          <div className="md:col-span-2 text-xs text-muted-foreground flex flex-wrap gap-3">
            <span>UID: <span className="font-mono">{row.adminUid}</span></span>
            {row.ip && <span>IP: {row.ip}</span>}
            <span>Action id: <span className="font-mono">{row.id}</span></span>
          </div>
        </div>
      )}
    </li>
  );
}
